// Lib
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, Box } from '@mui/material';

// Components
import Feather from '../../../components/Feather';
import Button from '../../../components/Button';

// Actions
import { SetShippingDetails } from '../../../actions/cart';
import { Close2Modal, Open2Modal } from '../../../actions/modal2';

// Hooks
import useUI from '../../../hooks/useUI';

// Constants
import {
  DAYS_RESTRICTION_EXPLANATION,
  DAYS_RESTRICTION_MESSAGE,
} from '../../../constants/common';

// Utils
import { pickIf } from '../../../utils/pickIf';

// Styles
import { useStyles } from './useScheduleDetailsStyles';

const MIN_MONTH_DAY_WITH_RESTRICTION = 29;
const MAX_MONTH_DAY_WITH_RESTRICTION = 31;
const RESTRICTION_MODAL_WIDTH_MOBILE = 268;
const RESTRICTION_MODAL_WIDTH_DESKTOP = 555;

const useScheduleDetails = () => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { shippingDetails, holidays } = useSelector((state) => ({
    shippingDetails: state.cart.shippingDetails,
    holidays: state.holidays,
  }));
  const { isMobileMode } = useUI();
  const restrictionModalWidth = pickIf(
    isMobileMode,
    RESTRICTION_MODAL_WIDTH_MOBILE,
    RESTRICTION_MODAL_WIDTH_DESKTOP,
  );

  const onChangeDate = useCallback(
    (newDate) => {
      dispatch(
        SetShippingDetails({
          type: 'date',
          value: newDate,
        }),
      );
    },
    [dispatch],
  );
  const onChangeMultipleSelect = useCallback(
    () =>
      ({ target }) => {
        const { value } = target;
        const checkedValues =
          typeof value === 'string' ? value.split(',') : value;
        dispatch(
          SetShippingDetails({
            type: 'weekly',
            value: {
              days: shippingDetails.weekly.days.map((item) =>
                checkedValues.includes(item.title)
                  ? { ...item, checked: true }
                  : { ...item, checked: false },
              ),
              monthNumber: shippingDetails.weekly.monthNumber,
            },
          }),
        );
      },
    [dispatch, shippingDetails.weekly],
  );
  const onChangeSelect = useCallback(
    (value, name) => () => {
      if (name === 'monthDay') {
        if (
          +value >= MIN_MONTH_DAY_WITH_RESTRICTION &&
          +value <= MAX_MONTH_DAY_WITH_RESTRICTION
        ) {
          dispatch(
            Open2Modal({
              id: 'fever-than-31',
              Component: () => (
                <>
                  <Box className={classes.titleContainer}>
                    <Feather
                      className={classes.icon}
                      type="error"
                      printType="stroke"
                    />
                    <Typography className={classes.monthTitle} variant="h3">
                      {DAYS_RESTRICTION_MESSAGE(`${value} Days`)}
                    </Typography>
                  </Box>
                  <Typography className={classes.monthMessage}>
                    {DAYS_RESTRICTION_EXPLANATION('Day')}
                  </Typography>
                  <Button
                    className={classes.button}
                    onClick={() => dispatch(Close2Modal())}>
                    Close
                  </Button>
                </>
              ),
              props: {
                paddingContent: {
                  xl: '30px 30px 30px 40px',
                  lg: '30px 30px 30px 40px',
                  md: '30px 16px 20px',
                },
              },
              closeIcon: true,
              styles: {
                minHeight: 'initial',
                width: restrictionModalWidth,
              },
            }),
          );
        }
        dispatch(
          SetShippingDetails({
            type: 'monthly',
            value: {
              ...shippingDetails.monthly,
              monthDay: {
                ...shippingDetails.monthly.monthDay,
                dayNumber: value,
              },
            },
          }),
        );
      }

      if (name === 'weekDayNumber') {
        dispatch(
          SetShippingDetails({
            type: 'monthly',
            value: {
              ...shippingDetails.monthly,
              weekDay: {
                ...shippingDetails.monthly.weekDay,
                dayNumber: value,
              },
            },
          }),
        );
        if (value === 'Fifth' && shippingDetails.monthly.weekDay.weekDay) {
          dispatch(
            Open2Modal({
              id: 'every-5-days',
              Component: () => (
                <>
                  <Box className={classes.titleContainer}>
                    <Feather
                      className={classes.icon}
                      type="error"
                      printType="stroke"
                    />
                    <Typography className={classes.monthTitle} variant="h3">
                      {DAYS_RESTRICTION_MESSAGE(
                        `5 ${shippingDetails.monthly.weekDay.weekDay}`,
                      )}
                    </Typography>
                  </Box>
                  <Typography className={classes.monthMessage}>
                    {DAYS_RESTRICTION_EXPLANATION(
                      shippingDetails.monthly.weekDay.weekDay,
                    )}
                  </Typography>
                  <Button
                    className={classes.button}
                    onClick={() => dispatch(Close2Modal())}>
                    Close
                  </Button>
                </>
              ),
              props: {
                paddingContent: {
                  xl: '30px 30px 30px 40px',
                  lg: '30px 30px 30px 40px',
                  md: '30px 16px 20px',
                },
              },
              closeIcon: true,
              styles: {
                minHeight: 'initial',
                width: restrictionModalWidth,
              },
            }),
          );
        }
      }
      if (name === 'weekDayTitle') {
        dispatch(
          SetShippingDetails({
            type: 'monthly',
            value: {
              ...shippingDetails.monthly,
              weekDay: {
                ...shippingDetails.monthly.weekDay,
                weekDay: value,
              },
            },
          }),
        );
        if (shippingDetails.monthly.weekDay.dayNumber === 'Fifth') {
          dispatch(
            Open2Modal({
              id: 'every-5-days',
              Component: () => (
                <>
                  <Box className={classes.titleContainer}>
                    <Feather
                      className={classes.icon}
                      type="error"
                      printType="stroke"
                    />
                    <Typography className={classes.monthTitle} variant="h3">
                      {DAYS_RESTRICTION_MESSAGE(`5 ${value}`)}
                    </Typography>
                  </Box>
                  <Typography className={classes.monthMessage}>
                    {DAYS_RESTRICTION_EXPLANATION(value)}
                  </Typography>
                  <Button
                    className={classes.button}
                    onClick={() => dispatch(Close2Modal())}>
                    Close
                  </Button>
                </>
              ),
              props: {
                paddingContent: {
                  xl: '30px 30px 30px 40px',
                  lg: '30px 30px 30px 40px',
                  md: '30px 16px 20px',
                },
              },
              closeIcon: true,
              styles: {
                minHeight: 'initial',
                width: restrictionModalWidth,
              },
            }),
          );
        }
      }
    },
    [dispatch, shippingDetails, classes, restrictionModalWidth],
  );
  const onChangeMonthCount = useCallback(
    (onChangeField) =>
      ({ value, name }) => {
        onChangeField(value);
        if (name === 'monthNumberForWeekly') {
          dispatch(
            SetShippingDetails({
              type: 'weekly',
              value: { ...shippingDetails.weekly, monthNumber: value },
            }),
          );
        }
        if (name === 'monthNumberForMonthDay') {
          dispatch(
            SetShippingDetails({
              type: 'monthly',
              value: {
                ...shippingDetails.monthly,
                monthDay: {
                  ...shippingDetails.monthly.monthDay,
                  monthNumber: value,
                },
              },
            }),
          );
        }
        if (name === 'monthNumberForWeekDay') {
          dispatch(
            SetShippingDetails({
              type: 'monthly',
              value: {
                ...shippingDetails.monthly,
                weekDay: {
                  ...shippingDetails.monthly.weekDay,
                  monthNumber: value,
                },
              },
            }),
          );
        }
      },
    [dispatch, shippingDetails],
  );
  const onChangeMonthlyOption = useCallback(
    (keyNumber) => () => {
      const options = Object.keys(shippingDetails.monthly);
      const uncheckedType = keyNumber === 0 ? options[1] : options[0];
      dispatch(
        SetShippingDetails({
          type: 'monthly',
          value: {
            ...shippingDetails.monthly,
            [uncheckedType]: {
              ...shippingDetails.monthly[uncheckedType],
              checked: false,
            },
            [options[keyNumber]]: {
              ...shippingDetails.monthly[options[keyNumber]],
              checked: true,
            },
          },
        }),
      );
    },
    [dispatch, shippingDetails.monthly],
  );
  return {
    onChangeDate,
    onChangeSelect,
    onChangeMultipleSelect,
    onChangeMonthlyOption,
    onChangeMonthCount,
    shippingDetails,
    holidays,
  };
};

export default useScheduleDetails;
